import React, { Suspense, useState } from 'react';
import Sidebar from '@/components/Sidebar/Sidebar';
import Header from '@/components/Header/Header';
import { ActionIcon, AppShell, Burger, Button, Center, Grid, Group, LoadingOverlay, Stack, Text, Tooltip } from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import logoBlue from '@/assets/logogovex_white.png';
import { RouteUtils } from '@bobex/react-core/routes/RouteUtils';
import {
  IconBell,
  IconCalendar,
  IconCurrencyEuro,
  IconDialpad,
  IconMessage,
  IconSearch,
  IconSeparator,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { AccountService } from '@bobex/react-core/services/AccountService';
import { useBoLoginInit } from '@bobex/react-core/utils/hooks/useBoLoginInit';
import LoadingScreen from '@bobex/react-core/components/LoadingScreen/LoadingScreen';
import { LanguagePicker } from '@bobex/react-core/components/LanguagePicker/LanguagePicker';
import { ColorSchemeToggle } from '@bobex/react-core/components/ColorSchemeToggle/ColorSchemeToggle';
import { Outlet } from 'react-router-dom';
import RightDrawer from '@/components/SearchHeader/RightDrawer';
import UserMenu from '@/components/UserMenu/UserMenu';

export default function AppLayout() {
  const { t } = useTranslation();
  const user = AccountService.getUser();
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState<'search' | 'cpv'>('search');

  const { i18n } = useTranslation();
  const locale = i18n.language.replace('_', '-');

  // Legacy -> React login handshake (one-shot ?token=...), see useBoLoginInit
  const { isLoading, failed: loginFailed, retry: retryLogin } = useBoLoginInit();
  useHotkeys([
    [
      'mod+K',
      () => {
        setRightPanelTab('search');
        setIsDrawerOpen((v) => !v);
      },
    ],
  ]);

  if (loginFailed) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="sm">
          <Text fw={600}>{t('login.boLoginFailedTitle', 'Could not sign you in automatically')}</Text>
          <Text size="sm" c="dimmed">
            {t('login.boLoginFailedText', 'The login handshake with the backoffice failed several times in a row.')}
          </Text>
          <Button onClick={retryLogin}>{t('login.boLoginRetry', 'Try again')}</Button>
        </Stack>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <LoadingOverlay
        visible={true}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ type: 'bars' }}
      />
    );
  }

  return (
    <AppShell
      bg={'bodyBackgroundColor'}
      header={{
        height: { base: 192, xs: 128, sm: 128, md: 128, lg: 128, xl: 64 },
      }}
      layout={'alt'}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
    >
      <AppShell.Header>
        <Grid
          align="center"
          h={'100%'}
          style={{
            padding: '0.8rem',
            zIndex: 1000,
            position: 'relative',
          }}
          grow
          gap={'sm'}
        >
          <Grid.Col span={4}>
            <Group wrap={'nowrap'}>
              <Group justify="flex-start" wrap={'nowrap'}>
                <Burger
                  opened={mobileOpened}
                  onClick={toggleMobile}
                  hiddenFrom="sm"
                  size="sm"
                  style={{ marginRight: '10px' }}
                />
                <Burger
                  opened={desktopOpened}
                  onClick={toggleDesktop}
                  visibleFrom="sm"
                  size="sm"
                  style={{ marginRight: '10px' }}
                />
              </Group>
              <Header />
            </Group>
          </Grid.Col>

          <Grid.Col span={4}>
            <Group wrap="nowrap">
              <ColorSchemeToggle />
              <React.Fragment>
                {new Intl.DateTimeFormat(locale, {
                  dateStyle: 'full',
                  timeZone: 'UTC',
                }).format(new Date())}
              </React.Fragment>
              <LanguagePicker />
            </Group>
          </Grid.Col>

          <Grid.Col span={4}>
            <Group wrap="nowrap">
              <Tooltip label={'ctrl+k'}>
                <ActionIcon
                  variant="default"
                  size="lg"
                  aria-label="Search"
                  onClick={() => {
                    setRightPanelTab('search');
                    setIsDrawerOpen((v) => !v);
                  }}
                >
                  <IconSearch stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <ActionIcon variant="default" size="lg" aria-label="Unpaid clients">
                <IconCurrencyEuro stroke={1.5} />
              </ActionIcon>
              <ActionIcon variant="default" size="lg" aria-label="notifications">
                <IconBell stroke={1.5} />
              </ActionIcon>
              <Tooltip label={t('Calendar')}>
                <ActionIcon
                  variant="default"
                  size="lg"
                  aria-label="Calendar"
                  component="a"
                  href={
                    user?.userID
                      ? RouteUtils.getBackofficeServletBasename() +
                        `/todo_calendar_call?userid=${user.userID}`
                      : '#'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconCalendar stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <ActionIcon variant="default" size="lg" aria-label="notifications">
                <IconMessage stroke={1.5} />
              </ActionIcon>
              <UserMenu />
              <Tooltip label={t('CPV codes', 'CPV codes')}>
                <ActionIcon
                  variant="default"
                  size="lg"
                  aria-label="CPV codes"
                  onClick={() => {
                    setRightPanelTab('cpv');
                    setIsDrawerOpen(true);
                  }}
                >
                  <IconDialpad stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Grid.Col>
        </Grid>
        <RightDrawer
          isVisible={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          activeTab={rightPanelTab}
        />
      </AppShell.Header>
      <AppShell.Navbar p="md" bg={'sidebarBackgroundColor'}>
        <AppShell.Section>
          <img src={logoBlue} alt="Govex" width={150} />
        </AppShell.Section>
        <AppShell.Section>
          <Sidebar />
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}

